import React, { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { fetchRealtimeToken } from '@query/realtime'
import { invalidateForRealtimeEvent, RealtimeEventMessage } from '@query/realtimeInvalidation'
import { clientMutationIdRegistry } from '@utils/clientMutationIdRegistry'

interface RealtimeProviderProps {
  slug: string
  children: React.ReactNode
}

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 20000]
const RESOURCE_EVENT_TYPES = new Set(['resource.updated', 'resource.created', 'resource.deleted'])
const SHOULD_LOG_CONNECTION_ERRORS = import.meta.env.DEV || import.meta.env.MODE === 'testing'

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const getWsUrl = (): string => {
  const base = import.meta.env.VITE_NOCFO_WS_BASE_URL || import.meta.env.VITE_NOCFO_BASE_URL || ''
  return base.replace(/\/$/, '').replace(/^http/, 'ws') + '/ws/events'
}

const parseEvent = (rawData: string, slug: string): RealtimeEventMessage | null => {
  try {
    const payload = JSON.parse(rawData) as RealtimeEventMessage
    if (!RESOURCE_EVENT_TYPES.has(payload.type)) return null
    if (payload.business_slug !== slug) return null
    return payload
  } catch {
    return null
  }
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ slug, children }) => {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!slug) return
    const wsUrl = getWsUrl()
    if (!/^wss?:\/\//.test(wsUrl)) return

    let stopped = false
    let socket: WebSocket | null = null
    let reconnectAttempt = 0

    const getReconnectDelay = () =>
      RECONNECT_DELAYS_MS[Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)]

    const handleMessage = (eventData: string) => {
      const payload = parseEvent(eventData, slug)
      if (!payload) return
      if (clientMutationIdRegistry.hasRegisteredClientMutationId(payload.client_mutation_id)) return
      void invalidateForRealtimeEvent(queryClient, payload)
    }

    const createSocketConnection = async (token: string) =>
      new Promise<void>(resolve => {
        socket = new WebSocket(wsUrl)

        socket.onopen = () => {
          socket?.send(JSON.stringify({ type: 'authenticate', token, business_slug: slug }))
          reconnectAttempt = 0
        }

        socket.onmessage = event => {
          handleMessage(event.data)
        }

        socket.onerror = () => socket?.close()
        socket.onclose = () => resolve()
      })

    const runConnectionLoop = async () => {
      while (!stopped) {
        try {
          const { token } = await fetchRealtimeToken({ slug })
          if (!token || stopped) return
          await createSocketConnection(token)
        } catch (error) {
          if (SHOULD_LOG_CONNECTION_ERRORS) {
            console.warn('[Realtime] Connection attempt failed', {
              slug,
              reconnectAttempt,
              error
            })
          }
        }

        if (stopped) return
        await sleep(getReconnectDelay())
        reconnectAttempt += 1
      }
    }

    void runConnectionLoop()

    return () => {
      stopped = true
      if (socket && socket.readyState < WebSocket.CLOSING) socket.close()
    }
  }, [queryClient, slug])

  return <>{children}</>
}
